(ns practitest.frontend.components.grid.filters
  (:require
   [clojure.set :as set]
   [clojure.string :as str]
   [practitest.frontend.common-entities.events :as common-events]
   [practitest.frontend.common.dom :as dom]
   [practitest.frontend.common.format-utils :as fu]
   [practitest.frontend.common.i18n :refer [get-translation] :as i18n]
   [practitest.frontend.common.mixpanel :as mixpanel]
   [practitest.frontend.components.grid.common :as gc]
   [practitest.frontend.components.grid.server :as server]
   [practitest.frontend.components.icons :as icons]
   [practitest.frontend.components.lists :as lists]
   [practitest.frontend.components.overlay :as overlay]
   [practitest.frontend.components.react-bootstrap :as react-bootstrap]
   [practitest.frontend.components.tooltip :as tooltip]
   [practitest.frontend.common.tooltip :as common-tooltip]
   [practitest.frontend.components2.misc-components :as misc-components]
   [practitest.frontend.routing.backend-routes :as broutes]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]))

(defonce ^:private filters-registry (atom {}))

(declare default-header-fn)

;;; API

;; view-fn - (fn [filterprops]) -> Reagent view for body
;; filtering-fn - (fn [filter-state column-info row]) -> boolean
;; description-fn - (fn [filter-state]) -> reagent view
;; header-fn - (fn [filterprops]) -> reagent view for header
(defn register-filter!
  ([type view-fn filtering-fn description-fn]
   (register-filter! type view-fn filtering-fn description-fn default-header-fn))
  ([type view-fn filtering-fn description-fn header-fn]
   (swap! filters-registry
          assoc type {:type type
                      :view-fn view-fn
                      :filtering-fn filtering-fn
                      :description-fn description-fn
                      :header-fn header-fn})))

(declare filter-filtering-fn)

(defn apply-filters
  [rows local-state-updating local-columns]
  (let [autofilters (:autofilters-state @local-state-updating)]
    (if (seq autofilters)
      (filter
        (fn [row]
          (or (:sticky-row? row)
              (reduce
                (fn [result [field autofilter]]
                  (let [column-info (first (filter #(= (:field %) field) local-columns))
                        filtering-fn (filter-filtering-fn column-info)]
                    (if (filtering-fn autofilter column-info row)
                      result
                      (reduced false))))
                true
                autofilters)))
        rows)
      rows)))

(defn transform-instance-for-testset-grid
  "Transforms a test instance for display in the testset grid.
   Formats the name field with display ID and test reference."
  [instance]
  (let [{:keys [test_display_id name display_id instance_display_id id]} instance
        instance-url (broutes/get-url-view "instances#edit" {:id id})]
    (-> instance
        (assoc :name {:url instance-url
                      :title (str "ID" display_id " "
                                  (i18n/get-translation "test.instance-of-test" "Instance of Test")
                                  " #" test_display_id " - " name)}
               :name-sort instance_display_id
               :display_id ""))))

(defn row-matches-filter?
  "Checks if a row matches a specific filter. Returns true if:
   - The column is found and the filtering function returns truthy
   - The column is not found (don't filter out rows for unknown fields)"
  [field filter-value columns-by-field row]
  (if-let [column-info (get columns-by-field field)]
    (let [filtering-fn (filter-filtering-fn column-info)]
      (filtering-fn filter-value column-info row))
    true))

(defn row-matches-all-filters?
  "Checks if a row matches all active filters."
  [filters columns-by-field row]
  (every? (fn [[field filter-value]]
            (row-matches-filter? field filter-value columns-by-field row))
          filters))

(defn filter-instances-for-testset
  "Filters instances for a single testset based on active filters.
   Returns filtered instances that match all filter criteria."
  [instances testset filters columns]
  (if-not (seq filters)
    instances
    (let [columns-by-field (into {} (map (juxt :field identity) columns))]
      (vec (filter
             (fn [instance]
               (let [instance-row (-> (transform-instance-for-testset-grid instance)
                                      (assoc :instance-row? true
                                             :testset_id (:id testset)))]
                 (row-matches-all-filters? filters columns-by-field instance-row)))
             instances)))))

;; Implementation
(defn on-filter-changed
  [tableprops]
  ;; Reset page number for filter changes
  (server/query-changed tableprops 1)

  ;; Reset selection for filter changes
  (when-let [toggle-selection (:toggle-selection tableprops)]
    ;; Force clearing of selection
    ;; TODO: configurable
    (when-not (= (:toggle-selection-on-filter-change? tableprops) false)
      (toggle-selection :all true)))

  (when-let [on-filter-changed (:on-filter-changed tableprops)]
    ;; TODO: pass filters state here ?
    (on-filter-changed))

  (when-let [autofilters-save (:autofilters-save tableprops)]
    (autofilters-save (:autofilters-state @(:local-state-updating tableprops))))

  ;; refresh PTQL preview
  (re-frame/dispatch [::common-events/refresh-effective-ptql
                      (:state-key tableprops)
                      (:autofilters-state @(:local-state-updating tableprops))]))

(defn- get-filter [type]
  (or (get @filters-registry type)
      (get @filters-registry :default)))

(defn filter-view-fn [column-info]
  (:view-fn (get-filter (:type column-info))))

(defn filter-header-fn [column-info]
  (:header-fn (get-filter (:type column-info))))

(defn- filter-filtering-fn [column-info]
  (:filtering-fn (get-filter (:type column-info))))

(defn filter-description-fn [column-info]
  (:description-fn (get-filter (:type column-info))))

(defn- substring-search [filter-state column-info row]
  (let [cell (gc/cell-text column-info row)]
    (fu/substring-search? cell filter-state)))

(defn- simple-value-info
  [filter-state]
  filter-state)

(defn- default-header-fn
  [{:keys [column]}]
  (str (gc/header-title column) " filter"))

(defn- simple-text-filter
  [{:keys [value
           on-change
           input-ref]}]
  [:input.form-control
   {:value (or value "")
    :ref (fn [node]
           (when node
             ;; Autofocus on mount
             (.focus node))

           (when input-ref
             (reset! input-ref node)))
    :on-key-down (fn [e])
    :on-change (fn [e]
                 (when on-change
                   (let [value (dom/event-value e)
                         value (if (str/blank? value)
                                 nil
                                 value)]
                     (on-change value))))}])

;; Default filter implementation
(register-filter!
  :default
  simple-text-filter
  substring-search
  simple-value-info)

(defn- exact-id-match? [cell value]
  ;; Exact word or hyphenated match, for example - when filtering over ID "1" - "PT-1" and "1" IDs should "pass"
  ;; while "PT-11" o1 "11" IDs shouldn't. ":" is also a suffix separator, for composite instance ids like "4:20".
  ;; For non-numeric queries (e.g. "KAN"), fall back to substring search so external ID prefixes match.
  (let [value-lower (.toLowerCase value)
        cell-lower (.toLowerCase cell)]
    (if (re-find #"\D" value)
      (str/includes? cell-lower value-lower)
      (let [regex (re-pattern (str "(^" value-lower "$|.*[-:]" value-lower "$)"))]
        (re-matches regex cell-lower)))))

(defn- exact-match-search [filter-state column-info row]
  ;; Raw field value, not gc/cell-text - cell-text truncates composite display ids for sorting.
  (let [cell (str (get row (:field column-info)))
        values (gc/parse-multiple-options filter-state)]
    (if (= 1 (count values))
      ;; For single value, perform an exact match (case-insensitive)
      (exact-id-match? cell (str (first values)))
      ;; For multiple values, check if any value matches exactly
      (some #(exact-id-match? cell (str %)) values))))

;; Special filter for external ID and ID - supports multiple filter options
(doseq [type [:display_id :external_id_or_display_id]]
  (register-filter!
    type
    simple-text-filter
    exact-match-search
    simple-value-info
    (fn [{:keys [column]}]
      [:div.external-id-filter-header
       [:span (str (gc/header-cell column) " filter")]
       [common-tooltip/info-circle-tooltip
        (get-translation "grid.external-id-filter.tooltip"
                         "You can filter by a specific ID / external ID, or a group of IDs using a comma-separated list")]])))

(defn single-list-filter
  [{:keys [value
           on-apply-filter
           on-change

           column

           get-field-props]}]
  (let [field (:field column)
        ;; TODO: seems really convoluted - think about something better
        field-props (when get-field-props
                      @(get-field-props field))
        options (if (= :linkedlist (:type column))
                  (sort (into #{} (mapcat second (:possible-values field-props))))
                  (:possible-values field-props))]
    [lists/simple-list {:clearable? true
                        :autofocus? true
                        :options    options
                        :value      (or value "")
                        :on-change  (fn [new-value]
                                      (when on-change
                                        (on-change new-value))
                                      (when on-apply-filter
                                        (on-apply-filter new-value)))}]))

(defn text-input-clearable
  [props]
  (let [on-clear (:on-clear props)]
    [:div {:style {:position "relative"}}
     [:input.form-control
      (dissoc props :on-clear)]

     (when on-clear
       [:div.input-clear-container
        [:span.input-clear-icon.clickable
         {:on-click (fn [e]
                      (when-let [on-clear (:on-clear props)]
                        (on-clear e)))}
         [icons/svg-x]]])]))

(defn multiple-list-filter-base
  [_]
  (let [local-state (reagent/atom {})]
    (fn [{:keys [value
                 on-change
                 options

                 label-fn
                 id-fn
                 search-fn]}]

      (let [value (or value #{})
            id-fn (or id-fn #(str "option-" %))
            search-fn (or search-fn (fn [value filter] (fu/substring-search? value filter)))
            has-filter? (> (count options) 20)
            filter-value (or (:filter-value @local-state) "")
            visible-options (vec (if (and (some? filter-value)
                                          (not (str/blank? filter-value)))
                                         (filter #(search-fn % filter-value) options)
                                         options))
            on-checked (fn [checked? option]
                         (when on-change
                           (let [new-value (if checked?
                                             (conj value option)
                                             (disj value option))
                                 new-value (if (empty? new-value)
                                             nil
                                             new-value)]

                             (on-change new-value))))]
        [:<>
         (let [visible-set (set visible-options)
               all-selected? (every? value visible-options)
               some-selected? (some value visible-options)]
           [:div.grid-filter-controls
            [misc-components/link-button
             {:disabled? all-selected?
              :on-click (fn [e]
                           (when on-change
                             (let [new-value (set/union value visible-set)]
                               (on-change (when (seq new-value) new-value)))))}
             (get-translation "generic.button.select-all" "Select All")]
            [misc-components/link-button
             {:class "pull-right"
              :disabled? (not some-selected?)
              :on-click (fn [e]
                          (when on-change
                            (let [new-value (set/difference value visible-set)]
                              (on-change (when (seq new-value) new-value)))))}
             (get-translation "generic.button.clear" "Clear")]])

         (when has-filter?
           [text-input-clearable
            {:class "mb-1"
             :type "text"
             :placeholder (get-translation "grid.filter-placeholder" "Filter...")
             :value filter-value
             :on-change (fn [e]
                          (swap! local-state assoc :filter-value (dom/event-value e)))
             :on-clear (fn [e]
                         (swap! local-state dissoc :filter-value))}])

         (into [:div.list-group.multiple-list-filter]
               (for [option visible-options
                     :let [selected? (contains? value option)
                           id (id-fn option)]]
                 ^{:key id}
                 [:div.list-group-item.d-flex
                  ;; TODO: highlight matching text
                  [label-fn id option]
                  [:div.form-switch.ms-auto
                   [:input.form-check-input.me-2
                    {:type      "checkbox"
                     :checked   selected?
                     :id        id
                     :on-change (fn [e]
                                  (on-checked (dom/event-value e) option))}]]]))]))))

(defn multiple-list-filter
  [{:keys [value
           on-change

           column
           get-field-props]}]
  (let [field (:field column)
        ;; TODO: seems really convoluted - think about something better
        field-props (when get-field-props
                      @(get-field-props field))
        raw-options (if (= :linkedlist (:type column))
                  (sort (into #{} (mapcat second (:possible-values field-props))))
                  (or (:possible-values field-props)
                      (:possible-values column)))
        filtered-options (filter #(not (str/blank? %)) raw-options)
        options (cons "" filtered-options)]
    [multiple-list-filter-base {:value     value
                                :on-change on-change
                                :options   options
                                :label-fn  (fn [id o]
                                             [:label.me-4 {:for id}
                                              (cond
                                                (str/blank? o)
                                                (str "( " (get-translation "grid.fast-filters.no-value" "No Value") " )")

                                                (vector? o)
                                                (last o)

                                                :else
                                                o)])}]))

(defn list-view-dispatcher-fn
  [{:keys [column]
    :as props}]
  (if (:multiple? column)
    (multiple-list-filter props)
    (single-list-filter props)))

(defn- list-value-info
  [filter-state]
  (cond
    (set? filter-state)
    (->> filter-state
         (map #(if (and (string? %) (empty? %))
                 (str "( " (get-translation "grid.fast-filters.no-value" "No Value") " )")
                 %))
         (str/join ", "))

    (and (string? filter-state)
         (empty? filter-state))
    (str "( " (get-translation "grid.fast-filters.no-value" "No Value") " )")

    :else
    filter-state))

(defn list-filtering-fn
  ;; TODO: change to cell-value instead of row ?
  [filter-state column-info row]
  (if (set? filter-state)
    (let [value (get row (:field column-info))
          lower-value (if (string? value) (str/lower-case value) value)
          lower-filter (set (map #(if (string? %) (str/lower-case %) %) filter-state))]
      (or (contains? lower-filter lower-value)
          ;; if filter-state includes "", then treat nil as a match
          (and (contains? lower-filter "")
               (nil? value))))
    (substring-search filter-state column-info row)))

(defn testing-scope-filtering-fn
  [filter-state column-info row]
  (let [testing-scope (get row (:field column-info))]
    (if (= testing-scope "LAST_RUN")
      (contains? filter-state :LAST_RUN)
      (contains? filter-state :TESTSETS))))

(register-filter!
  :list
  list-view-dispatcher-fn
  list-filtering-fn
  list-value-info)

(register-filter!
  :linkedlist
  list-view-dispatcher-fn
  list-filtering-fn
  list-value-info)

(defn multilist-filtering-fn
  [filter-state column-info row]
  (let [cell-value (get row (:field column-info))
        lower-filter (set (map #(if (string? %) (str/lower-case %) %) filter-state))]
    (or
      ;; if filter-state includes "" and the field is nil, count it as a match
      (and (contains? lower-filter "")
           (nil? cell-value))
      (boolean (some #(contains? lower-filter
                                 (if (string? %) (str/lower-case %) %))
                     cell-value)))))

(register-filter!
  :multilist
  list-view-dispatcher-fn
  multilist-filtering-fn
  list-value-info)

(doseq [type [:hierarchical-name :global-field-name]]
  (register-filter!
    type
    simple-text-filter
    (fn [filter-state column-info row]
      (let [name-obj (get row (:field column-info))
            title (:title name-obj)]
        (fu/substring-search? title filter-state)))
    simple-value-info))

(register-filter!
  :testset-instance-run-status
  list-view-dispatcher-fn
  list-filtering-fn
  list-value-info)

;; Custom filter function for folder_id that handles both name and ID
(defn folder-id-filtering-fn
  [filter-state column-info row] 
  (let [field      (:field column-info)
        field-ns   (namespace field)
        folder-key (if field-ns (keyword field-ns "folder") :folder)
        folder-name (get-in row [folder-key :name])
        folder-id   (str (get row field ""))] 
    (if (seq folder-name)                  
      (fu/substring-search? folder-name filter-state)
      (fu/substring-search? folder-id filter-state))))


(register-filter!
 :folder_id
 simple-text-filter
 folder-id-filtering-fn
 simple-value-info)


;; Autofilters state
(comment
  {:filters-visible?  true
   :autofilters-state {:col1 "123"}})

(defn filter-popover [_ _]
  (let [filter-state (reagent/atom {})]
    (fn [{:keys [local-state-updating
                 get-field-props
                 table-container-ref]
          :as tableprops}
         column]

      ;; TODO: controllable with props ?
      (let [field (:field column)
            has-filters? (some? (get (:autofilters-state @local-state-updating) field))
            table-state (get (:autofilters-state @local-state-updating) (:field column))]
        [overlay/overlay
         ;; TODO: auto switch between left/right depending on pointer position ?
         {:placement     "right-start"
          :container-ref table-container-ref
          :overlay-state filter-state}
         [:span.filter-icon
          {:class    (dom/classes-pairs :has-filters has-filters?)
           :on-click (fn [e]
                       (mixpanel/track "Grid filters" {"Entity" "Testset"})
                       (overlay/on-click-handler filter-state e)
                       ;; Init state from table on opening
                       (swap! filter-state assoc :value table-state))}
          [icons/svg-funnel-fill]]

         (let [apply-filter (fn [new-filter]
                              (if (nil? new-filter)
                                ;; Clear filter
                                (swap! local-state-updating update :autofilters-state dissoc field)
                                ;; Enable filter
                                (swap! local-state-updating assoc-in [:autofilters-state field] new-filter))

                              (on-filter-changed tableprops))]

           [react-bootstrap/popover
            {:on-click    (fn [e]
                            ;; Otherwise click is propagated to parent span
                            (dom/stop-propagation e))

             :on-key-down (fn [e]
                            ;; Enter
                            (when (= (.-keyCode e) 13)
                              (apply-filter (:value @filter-state))
                              ;; TODO: controllable by options ?
                              (overlay/hide! filter-state)
                              ))}
            [:div.popover-header
             (let [header-fn (filter-header-fn column)]
               [header-fn {:column column
                           :value (:value @filter-state)}])]
            [:div.popover-body.autofilter-popover
             (let [view-fn (filter-view-fn column)]
               [view-fn {:value           (:value @filter-state)
                         :column          column
                         :get-field-props get-field-props
                         :on-change       (fn [new-value]
                                            (swap! filter-state assoc :value new-value))
                         ;; TODO: enable/disable ?
                         ;:on-apply-filter apply-filter
                         }])]
            [:div.popover-footer
             {:style {:display "flex"}}
             [:div.btn-group.ms-auto
              [:button.btn.btn-sm.btn-pt-secondary
               {:on-click (fn [e]
                            (swap! filter-state dissoc :value)
                            ;; This will clear filter
                            (apply-filter nil)
                            (overlay/hide! filter-state)
                            )}
               (if (nil? (:value @filter-state))
                 "Cancel"
                 "Clear")]
              [:button.btn.btn-sm.btn-primary
               {:on-click (fn [e]
                            (apply-filter (:value @filter-state))
                            (overlay/hide! filter-state)
                            )}
               "Apply"]]]])]))))

(defn- generate-filters-info
  [tableprops]
  (let [local-state-updating (:local-state-updating tableprops)
        local-columns (:local-columns tableprops)
        autofilters-state (:autofilters-state @local-state-updating)]
    (for [[col filter-state] autofilters-state]
      (let [column-info (first (filter #(= (:field %) col) local-columns))
            description-fn (filter-description-fn column-info)]
        ^{:key (str (:field column-info))}
        [:span.badge.bg-light.text-dark
         (str (gc/header-title column-info) ": ")

         [description-fn filter-state]

         [tooltip/hover-tooltip
          "Remove filter"
          [:span.remove-filter
           {:on-click (fn [e]
                        (mixpanel/track "Remove filter" {"Entity" "Testset"})
                        (swap! local-state-updating update :autofilters-state dissoc col)
                        (on-filter-changed tableprops))}
           [icons/svg-x]]]]))))

(defn filter-information-popover [{:keys [local-state-updating
                                          table-container-ref]
                                   :as   tableprops}]
  (let [autofilters-state (:autofilters-state @local-state-updating)
        has-filters? (seq autofilters-state)]
    [overlay/overlay
     {:placement     "left-start"
      :container-ref table-container-ref}

     (fn [overlay-state]
       [:button.btn.btn-sm.btn-outline-pt-secondary
        {:on-click (do
                     (mixpanel/track "Filters Information" {"Entity" "Testset"})
                     (overlay/make-on-click-handler overlay-state))
         :disabled (not has-filters?)}
        [icons/svg-chevron-down]])

     [react-bootstrap/popover
      {:class "filters-info-popover"}
      [:div.popover-header
       "Filters information"]
      [:div.popover-body
       (generate-filters-info tableprops)]]]))

(defn filters-info-block
  [{:keys [local-state-updating] :as tableprops}]
  (let [autofilters-state (:autofilters-state @local-state-updating)
        active-filters (count (seq autofilters-state))
        has-filters? (> active-filters 0)]
    (when has-filters?
      [:div.centered
       ;; TODO: badge with filters counter ?
       "Filtered by: "
       ;; We're hiding extra span's with CSS
       (into [:div.filter-badges]
             (generate-filters-info tableprops))
       ;; TODO: detect overflow and display this only on ellipsis appearance
       [filter-information-popover tableprops]
       [:button.btn.btn-sm.btn-outline-pt-secondary.nowrap
        {:on-click (fn [e]
                     (swap! local-state-updating assoc :autofilters-state {})
                     (on-filter-changed tableprops))
         :disabled (not has-filters?)}
        [icons/svg-arrow-counterclockwise] " Reset filters"]])))
